import React, { useState } from 'react';
import {
  Button,
  Col,
  Form,
  message,
  notification,
  Popconfirm,
  Row,
  Table,
  Tag,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  FileSyncOutlined,
} from '@ant-design/icons';
import { SearchBar } from '../Atoms/SearchBar';
import { aSearchElements, oInitState } from './RegimeConstants';
import { generateQueries } from '../../Utils/query';
import { useFetchRegimes } from '../../Hooks/Regime.hook';
import { RegimeActions } from './RegimeActions';
import { process, SAVE, UPDATE } from '../../Service/Api';

export const RegimeTable = () => {
  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [search, setSearch] = useState(oInitState);
  const [regimes, loading, change, updater] = useFetchRegimes();
  const [formRef] = Form.useForm();

  const handleDeactivate = async sId => {
    const response = await process(
      UPDATE,
      'regimes',
      { status: 0 },
      { id: sId }
    );
    if (response?.ok) {
      message.success('Desactivado correctamente');
      updater();
    } else {
      message.error('Error al desactivar');
    }
  };

  const aColumns = [
    {
      dataIndex: 'code',
      title: 'Regimen',
    },
    {
      dataIndex: 'description',
      title: 'Descripcion',
    },
    {
      dataIndex: 'status',
      title: 'Estatus',
      render: status =>
        status ? (
          <Tag color="#87d068">Activo</Tag>
        ) : (
          <Tag color="#f50">Inactivo</Tag>
        ),
    },
    {
      dataIndex: 'id',
      title: 'Acciones',
      render: (_, row) => {
        return (
          <Row>
            <Button
              onClick={() => {
                setSelected(row);
                formRef.setFieldsValue(row);
                setModal(true);
              }}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              disabled
              onConfirm={() => handleDeactivate(row.id)}
              title="Está seguro de desactivar este Regimen?"
            >
              <Button disabled>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Row>
        );
      },
    },
  ];

  const handleReset = () => {
    setSearch(oInitState);
    change();
  };

  const handleSearch = () => change(generateQueries(search, aSearchElements));

  const handleSubmit = async values => {
    setModalLoading(true);
    let response;
    let oSend = { ...values };
    if (selected?.id) {
      response = await process(UPDATE, 'regimes', oSend, {
        id: selected.id,
      });
    } else {
      response = await process(SAVE, 'regimes', oSend);
    }
    setModalLoading(false);
    if (response?.ok) {
      message.success('Exito');
      formRef.resetFields();
      setSelected({});
      setModal(false);
      updater();
    } else {
      const { data } = response;
      if (data?.code === 400) {
        let sErrors = '';
        if (Array.isArray(data.errors)) {
          for (const oError of data.errors) {
            if (oError.type === 'unique violation') {
              sErrors += `El valor ${oError.path} ya existe en BD\n`;
            }
          }
        } else {
          for (const sKey in data?.errors) {
            sErrors += data.errors[sKey] + '\n';
          }
        }
        message.error(data?.message);
        if (sErrors !== '') {
          notification.error({
            message: sErrors,
            title: 'Errores',
          });
        }
      } else if (data?.code === 409) {
        message.error('Valor duplicado');
      } else {
        message.error('Error en planes');
      }
    }
  };

  return (
    <div>
      <Row justify="space-between">
        <Col className="screen-title">
          <h3>Regimenes Fiscales</h3>
          <h3>
            <Tag>
              {regimes?.total >= 0 ? regimes.total : <LoadingOutlined />}
            </Tag>
          </h3>
        </Col>
        <Col>
          <Button onClick={() => setModal(true)} type="primary">
            Crear <FileSyncOutlined />
          </Button>
        </Col>
      </Row>
      <RegimeActions
        fn={{
          handler: setModalLoading,
          handlerSelected: setSelected,
          selected,
        }}
        modal={{
          form: formRef,
          handler: setModal,
          loading: modalLoading,
          submit: handleSubmit,
          visible: modal,
        }}
      />
      <SearchBar
        elements={aSearchElements}
        {...{
          handleReset,
          handleSearch,
          search,
          setSearch,
        }}
      />
      <Table
        columns={aColumns}
        dataSource={regimes?.data}
        loading={loading}
        pagination={{
          current: regimes?.skip ? regimes.skip / 10 + 1 : 1,
          onChange: e =>
            change(
              generateQueries(search, aSearchElements),
              (e - 1) * regimes.limit
            ),
          pageSizeOptions: [10],
          total: regimes.total,
        }}
        rowKey={row => row.id}
      />
    </div>
  );
};
